import React, { useContext, useState } from 'react';
import compareDesc from 'date-fns/compareDesc';
import parseISO from 'date-fns/parseISO';
import { useAsyncEffect } from '@app/hooks';
import { AccountContext } from '../AccountContext';
import { toFormattedDate, toFormattedRate } from '@app/utilities';

export default function AccountRateHistory({ refreshToken }: { refreshToken?: number }) {
  const { accountId, interestService } = useContext(AccountContext);
  const [accountEffectiveRates, setAccountEffectiveRates] = useState({} as any);
  const [accountSubledgerRates, setAccountSubledgerRates] = useState({} as any);

  const updateAccountRateHistory = async () => {
    try {
      const accountInterestRateHistory = await interestService.getAccountRateHistory(accountId);
      const effectiveRates = accountInterestRateHistory.rates;
      const subledgerRates = accountInterestRateHistory.subledgerRates;

      setAccountEffectiveRates({ loaded: true, effectiveRates });
      setAccountSubledgerRates({ loaded: true, subledgerRates });
    } catch (ex) {
      await updateAccountRateHistory();
    }
  };

  useAsyncEffect(async () => {
    await updateAccountRateHistory();
  }, [accountId, refreshToken]);

  return (
    <>
      <h2>Effective Rate History</h2>

      {!accountEffectiveRates.loaded && <div className="ui active centered inline loader" />}

      {accountEffectiveRates.loaded && accountEffectiveRates.effectiveRates.length == 0 && <p>No Rate History</p>}

      {accountEffectiveRates.loaded && accountEffectiveRates.effectiveRates.length > 0 && (
        <table className="ui compact table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: '10%' }}>Interest reference date</th>
              <th style={{ width: '70%' }}>Effective rates</th>
            </tr>
          </thead>
          <tbody>
            {accountEffectiveRates.effectiveRates.map((rate) => (
              <tr key={rate.accountInterestReferenceDate}>
                <td data-label="accountInterestReferenceDate">{toFormattedDate(rate.accountInterestReferenceDate)}</td>
                <td style={{ padding: 15 }}>
                  <div className="ui horizontal cards">
                    {rate.effectiveRates.map((r) => (
                      <div className="ui label" style={{ margin: 5 }} key={r.SpendType}>
                        <div style={{ 'margin-bottom': 5, 'border-bottom': 'light grey' }}>
                          Spend Type: {r.spendType}
                        </div>
                        {r.feeCategory && (
                          <div style={{ 'margin-bottom': 5, 'border-bottom': 'light grey' }}>
                            Fee Category: {r.feeCategory}
                          </div>
                        )}
                        {r.subledgerType && (
                          <div style={{ 'margin-bottom': 5, 'border-bottom': 'light grey' }}>
                            Subledger Type: {r.subledgerType}
                          </div>
                        )}
                        {r.updateReason && (
                          <div style={{ 'margin-bottom': 5, 'border-bottom': 'light grey' }}>
                            Update Reason: {r.updateReason}
                          </div>
                        )}
                        <div style={{ 'margin-bottom': 5 }}>
                          Go To Rate: {r.derivedGoToRate.air} (Effective {toFormattedDate(r.goToRate.effectiveDate)})
                        </div>
                        {r.baseRate && (
                          <div style={{ 'margin-bottom': 5 }}>
                            Base Rate: {toFormattedRate(r.baseRate.rate)} (Effective{' '}
                            {toFormattedDate(r.baseRate.effectiveDate)})
                          </div>
                        )}
                        {r.baseRate && (
                          <div style={{ 'margin-bottom': 5 }}>Variance: {toFormattedRate(r.variance)} </div>
                        )}
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="ui divider" />

      <h2>Subledger Rate History</h2>

      {!accountSubledgerRates.loaded && <div className="ui active centered inline loader" />}

      {accountSubledgerRates.loaded && accountSubledgerRates.subledgerRates.length == 0 && <p>No Rate History</p>}

      {accountSubledgerRates.loaded &&
        accountSubledgerRates.subledgerRates.length > 0 &&
        accountSubledgerRates.subledgerRates.map((subledger) => (
          <>
            <div
              className="sub header"
              style={{ 'border-bottom': 'none', paddingLeft: '0', paddingRight: '0', paddingBottom: '0' }}>
              <span style={{ 'font-weight': 'bold' }}>{`
              ${subledger.subledgerType
                ? subledger.subledgerType + (accountEffectiveRates.effectiveRates.at(0).effectiveRates.find((r) => r.subledgerType == subledger.subledgerType) ? '' : ' (Rate defaulted to spend type & fee category)')
                : subledger.spendType + (subledger.feeCategory ? ' - ' + subledger.feeCategory + ' Fee' : '')}`}
              </span>
              <span style={{ 'border-bottom': 'none', padding: '0' }}>{` (${subledger.subledgerId})`}</span>
              <table className="ui compact table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>Updated Date</th>
                    <th style={{ width: '10%' }}>Effective Rate</th>
                    <th style={{ width: '20%' }}>Update Reason</th>
                    <th style={{ width: '10%' }}>Go To Rate</th>
                    <th style={{ width: '10%' }}>Expiry Date</th>
                    <th style={{ width: '10%' }}>Base Rate Type</th>
                    <th style={{ width: '10%' }}>Historic Base Rate</th>
                    <th style={{ width: '10%' }}>Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {subledger.effectiveRates
                    .sort((a, b) => compareDesc(parseISO(a.changedDate), parseISO(b.changedDate)))
                    .map((rate) => (
                      <tr key={rate.updatedDate}>
                        <td data-label="updatedDate">{rate.updatedDate}</td>
                        <td data-label="effectiveRate">{rate.derivedEffectiveRate.air}</td>
                        <td data-label="updateReason">{rate.updateReason ?? 'N/A'}</td>
                        <td data-label="goToRate">{rate.goToRate ? rate.derivedGoToRate.air : 'N/A'}</td>
                        <td data-label="rateExpiry">{rate.expiryDate ?? 'N/A'}</td>
                        <td data-label="baseRateType">{rate.rateType ?? 'N/A'}</td>
                        <td data-label="historicBaseRate">{rate.baseRate ? toFormattedRate(rate.baseRate) : 'N/A'}</td>
                        <td data-label="variance">{rate.variance ? toFormattedRate(rate.variance) : 'N/A'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <div className="ui divider" />
            </div>
          </>
        ))}
    </>
  );
}
